import React, { useCallback, useEffect, useState } from "react";
import Modal from "../../components/Modal";
import PasswordInput from "../../components/PasswordInput";
import Loader, { Spinner } from "../../components/Loader";
import { errorMessage } from "../../services";
import { createSupportAdmin, deleteSupportAdmin, getSupportAdmins, updateSupportAdmin } from "../../services/supportAdmins";
import { startViewAs } from "../../utils/viewAs";

// Super Admin: Ownezzy's support team. Each Support Admin signs in with the password set here; they can't change or
// reset it, so they ask the Super Admin, who can see it below or set a new one. What they can open is listed in
// ACCESS (enforced by the backend's middleware/roles.js limitSupport).
const ACCESS = [
  "All websites: edit a website (plans show Free trial or Paid, no amounts)",
  "Customers: edit details and reset a password",
  "Analytics: Admin Tracker only",
  "Suggestions, Profile and Download app",
];

function SupportAdminModal({ member, onClose, onSaved }) {
  const editing = Boolean(member);
  const [form, setForm] = useState({
    name: member ? member.name : "",
    email: member ? member.email : "",
    phone: member ? member.phone : "",
    password: member ? member.password : "",
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const payload = { name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim() };
      // Sent only when it is new or changed: a new password signs them out everywhere
      if (!editing || form.password !== member.password) payload.password = form.password;
      const { data } = editing ? await updateSupportAdmin(member.id, payload) : await createSupportAdmin(payload);
      onSaved(data.message);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal eyebrow="SUPPORT ADMIN" title={editing ? `Edit ${member.name}` : "Add Support Admin"} onClose={onClose}
      description="They sign in with this email and password, and can't change the password themselves.">
      <form onSubmit={submit}>
        {error && <div className="form-error">{error}</div>}
        <label className="admin-field">Full name<input required minLength={2} maxLength={80} value={form.name} onChange={set("name")} /></label>
        <div className="form-row">
          <label className="admin-field">Email<input required type="email" value={form.email} onChange={set("email")} /></label>
          <label className="admin-field">Phone<input value={form.phone} onChange={set("phone")} inputMode="tel" placeholder="10-digit mobile" /></label>
        </div>
        <label className="admin-field">Password <small className="field-help">At least 8 characters.{editing ? " Changing it signs them out everywhere." : ""}</small>
          <PasswordInput required minLength={8} maxLength={100} value={form.password} onChange={set("password")} autoComplete="new-password" />
        </label>
        <div className="product-modal-actions">
          <button type="button" className="outline-btn" onClick={onClose}>Cancel</button>
          <button className="primary-btn" disabled={busy}>{busy ? <><Spinner /> Saving</> : editing ? "Save" : "Add Support Admin"}</button>
        </div>
      </form>
    </Modal>
  );
}

// The password, hidden until the eye is tapped, with a copy button
function SavedPassword({ value, notify }) {
  const [shown, setShown] = useState(false);
  if (!value) return <small className="cell-sub">Not available: set a new one</small>;
  const copy = () => navigator.clipboard.writeText(value).then(() => notify("Password copied")).catch(() => notify("Could not copy", "error"));
  return (
    <span className="support-password">
      <code>{shown ? value : "••••••••"}</code>
      <button type="button" className="text-btn" onClick={() => setShown((s) => !s)}>{shown ? "Hide" : "Show"}</button>
      <button type="button" className="text-btn" onClick={copy}>Copy</button>
    </span>
  );
}

export default function SupportAdminsTab({ notify }) {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modal, setModal] = useState(null); // "new" or a Support Admin
  const [removing, setRemoving] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    getSupportAdmins()
      .then(({ data }) => { setTeam(data.supportAdmins); setError(""); })
      .catch((err) => setError(errorMessage(err)))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleStatus = async (m) => {
    try {
      const { data } = await updateSupportAdmin(m.id, { status: m.status === "active" ? "inactive" : "active" });
      notify(data.message);
      load();
    } catch (err) {
      notify(errorMessage(err), "error");
    }
  };
  const remove = async () => {
    try {
      const { data } = await deleteSupportAdmin(removing.id);
      notify(data.message);
      setRemoving(null);
      load();
    } catch (err) {
      notify(errorMessage(err), "error");
    }
  };

  return (
    <>
      <div className="page-heading">
        <div>
          <p className="eyebrow">OWNEZZY TEAM</p>
          <h1>Support admins</h1>
          <p className="subheading">People who help customers. They sign in with the password you set here and can only open:</p>
          <ul className="support-access">{ACCESS.map((a) => <li key={a}>{a}</li>)}</ul>
        </div>
        <button className="primary-btn" onClick={() => setModal("new")}>＋ Add Support Admin</button>
      </div>

      {error && <div className="admin-error">{error}</div>}
      {loading && !team.length && <Loader label="Loading support admins" />}
      {!loading && !team.length && !error && (
        <div className="panel staff-empty">
          <span aria-hidden="true">🎧</span>
          <b>No Support Admins yet</b>
          <p>Add someone to help customers with their websites.</p>
          <button className="primary-btn" onClick={() => setModal("new")}>＋ Add Support Admin</button>
        </div>
      )}

      <div className="staff-grid">
        {team.map((m) => (
          <article className={`staff-card ${m.status === "active" ? "" : "is-paused"}`} key={m.id}>
            <div className="staff-card-top">
              <span className="staff-avatar" aria-hidden="true">{m.name.charAt(0).toUpperCase()}</span>
              <div><b>{m.name}</b><small>{[m.email, m.phone].filter(Boolean).join(" · ")}</small></div>
              <span className={`status-pill ${m.status === "active" ? "status-on" : "status-off"}`}><i />{m.status === "active" ? "Active" : "Paused"}</span>
            </div>
            <div className="support-password-row"><span className="cell-sub">Password</span> <SavedPassword value={m.password} notify={notify} /></div>
            <div className="row-actions staff-actions">
              <button className="edit-action" onClick={() => setModal(m)}>Edit</button>
              <button className="toggle-action" onClick={() => toggleStatus(m)}>{m.status === "active" ? "Pause" : "Resume"}</button>
              {m.status === "active" && <button className="view-as-action" onClick={() => startViewAs(m.id).catch((err) => notify(errorMessage(err), "error"))}>👁 View as</button>}
              <button className="delete-action" onClick={() => setRemoving(m)}>Delete</button>
            </div>
          </article>
        ))}
      </div>

      {modal && (
        <SupportAdminModal member={modal === "new" ? null : modal} onClose={() => setModal(null)}
          onSaved={(message) => { setModal(null); notify(message); load(); }} />
      )}
      {removing && (
        <Modal eyebrow="DELETE SUPPORT ADMIN" title={`Delete ${removing.name}?`} onClose={() => setRemoving(null)}>
          <p className="muted">They are signed out at once and can no longer log in.</p>
          <div className="product-modal-actions">
            <button className="outline-btn" onClick={() => setRemoving(null)}>Cancel</button>
            <button className="danger-btn" onClick={remove}>Delete</button>
          </div>
        </Modal>
      )}
    </>
  );
}
